import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import ContactlessIcon from '@mui/icons-material/Contactless';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReportIcon from '@mui/icons-material/Report';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import parsePhoneNumber from 'libphonenumber-js';
import debounce from 'lodash.debounce';
import {
	ConditionalWrapper,
	DataGrid,
	FilterAutoCompleteById,
	FilterSearch,
	FilterSelect,
	GroupChip,
	RoleChip,
	SupervisorChip,
	TaskDialog,
	AddToRoleDialog,
	ConfirmDialog,
	DocumentViewer,
	GeneralTooltip as Tooltip,
} from '@repo/components';
import {
	PERMISSIONS,
	Task,
	User,
	Profile,
	Role,
	RoleWithTasks,
	Document,
	DocumentType,
	Group,
	InviteData,
} from '@repo/shared/domain';
import { InviteForm, UserForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { toFormattedHex } from 'src/helpers/string';
import CascadingRoleRevocationAlert from 'src/components/CascadingRoleRevocationAlert';
import BadgeDialog from 'src/parts/users/BadgeDialog';
import { calculateCascadingRoleRevocations } from 'src/helpers/calculateCascadingRoleRevocations';
import { FileState } from 'src/parts/tabs/DocumentTab';
import { getMyGroups, getGroupRoles } from 'src/api/supervisor/groups';
import {
	getAllSupervisorUsers,
	getUserProfile,
	getGroupUserTasks,
	getGroupUserApprovals,
	getGroupUserRolesWithTasks,
	getGroupUserDocuments,
	addRolesToUser,
	revokeRolesFromUser,
	updateUserData as apiUpdateUserData,
	updateBadgeId,
	restoreUser as apiRestoreUser,
	inviteUser,
	deleteUser,
} from 'src/api/supervisor/users';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import UserRolesWithTasksAccordion from 'src/parts/users/UserRolesWithTasksAccordion';
interface SearchParams {
	search?: string;
	roleIds?: string;
	groupId?: string;
	supervisorId?: string;
	page?: number;
	sort?: string;
	status?: 'active' | 'archived';
	userId?: string;
}
export const Route = createFileRoute('/supervisor_/users')({
	loader: async () => {
		const groups = await getMyGroups();
		return { groups };
	},
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		search: search.search as string | undefined,
		roleIds: search.roleIds as string | undefined,
		groupId: search.groupId as string | undefined,
		supervisorId: search.supervisorId as string | undefined,
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
		status: search.status as 'active' | 'archived' | undefined,
		userId: search.userId as string | undefined,
	}),
	component: SupervisorUsersPage,
});

const colors: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
	done: 'success',
	accepted: 'success',
	pending: 'default',
	todo: 'default',
	locked: 'default',
	expiryWarning: 'warning',
	expired: 'error',
	failed: 'error',
	inReview: 'error',
	declined: 'error',
};

const states: Record<string, string> = {
	done: 'Done',
	pending: 'Pending',
	todo: 'Todo',
	locked: 'Approval needed',
	expiryWarning: 'Almost Expiring',
	expired: 'Expired',
	failed: 'Failed',
	superseded: 'Superseded',
};

function SupervisorUsersPage() {
	const { groups } = Route.useLoaderData();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const defaultCountry = config?.defaultCountry || 'BE';

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const usesRFID = config?.featureFlags?.USES_RFID || false;
	const isBasf = config?.featureFlags?.IS_BASF || false;
	const usesSupervisor = config?.featureFlags?.USES_SUPERVISOR || false;
	const showSupervisor = isBasf && usesSupervisor;
	const inviteGroup = groups.find((g: Group) => g.id === authenticatedUser?.groupIds?.[0]) || groups[0];

	const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
	const [activeUser, setActiveUser] = useState<User | undefined>();
	const [activeUserGroupId, setActiveUserGroupId] = useState<string | undefined>();
	const [activeUserTasks, setActiveUserTasks] = useState<Task[]>([]);
	const [activeUserProfile, setActiveUserProfile] = useState<Profile>();
	const [activeUserTodoTasks, setActiveUserTodoTasks] = useState<Task[]>([]);
	const [activeUserApprovals, setActiveUserApprovals] = useState<Task[]>([]);
	const [activeUserTodoApprovals, setActiveUserTodoApprovals] = useState<Task[]>([]);
	const [rolesWithTasks, setRolesWithTasks] = useState<RoleWithTasks[]>([]);
	const [activeUserDocuments, setActiveUserDocuments] = useState<Document[]>([]);
	const [selectedFile, setSelectedFile] = useState<Document | null>(null);
	const [activeTask, setActiveTask] = useState<Task | undefined>();
	const [isEditingRoles, setIsEditingRoles] = useState(false);
	const [isEditingUser, setIsEditingUser] = useState(false);
	const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
	const [roleToRevokeData, setRoleToRevokeData] = useState<
		| {
				roleId: string;
				roleName: string;
				cascadingRoles: Role[];
		  }
		| undefined
	>();
	const [isRoleDialogOpen, setRoleDialogOpen] = useState(false);
	const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState(false);
	const [isDrawerLoading, setIsDrawerLoading] = useState(false);
	const [isRestoreUserDialogOpen, setIsRestoreUserDialogOpen] = useState(false);
	const [activeGroupRoles, setActiveGroupRoles] = useState<Role[]>([]);

	const userStatusFilter = search.status === 'archived' ? 'archived' : 'active';

	const {
		data: usersData,
		isLoading,
		refetch: refetchUsers,
	} = useQuery({
		queryKey: [
			'supervisor-all-users',
			search.search,
			search.roleIds,
			search.groupId,
			search.supervisorId,
			search.page,
			search.sort,
			search.status,
		],
		queryFn: () =>
			getAllSupervisorUsers({
				search: search.search,
				roleIds: search.roleIds,
				groupId: search.groupId,
				supervisorId: search.supervisorId,
				page: search.page,
				sort: search.sort || 'updatedAt:desc',
				archived: `${userStatusFilter === 'archived'}`,
			}),
	});

	const { data: documentTypesResponse } = useQuery({
		queryKey: ['document-types', 'users'],
		queryFn: () => getDocumentTypes('users'),
	});
	const documentTypes = documentTypesResponse?.data || [];

	useEffect(() => {
		if (!activeUser || !usersData?.data) return;
		setActiveUser(usersData.data.find(({ id }) => id === activeUser.id));
	}, [activeUser, usersData?.data]);

	const revalidateUsers = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['supervisor-all-users'] });
		refetchUsers();
	}, [queryClient, refetchUsers]);

	const getUserGroupId = (user: User): string | undefined => {
		const supervisorGroupIds = new Set(groups.map((g: Group) => g.id));
		return user.groupIds?.find((gid) => supervisorGroupIds.has(gid));
	};

	const handleRowClick = useCallback(
		async ({ id }: { id: string }) => {
			const user = usersData?.data.find(({ id: userId }) => userId === id);
			if (!user) return;
			setActiveUser(user);
			setIsDrawerLoading(true);

			const groupId = getUserGroupId(user);
			setActiveUserGroupId(groupId);

			if (!groupId) {
				setIsDrawerLoading(false);
				return;
			}

			try {
				const [
					userTasksResponse,
					userApprovalsResponse,
					userProfileResponse,
					userRolesWithTasksResponse,
					groupRoles,
					userDocuments,
				] = await Promise.all([
					getGroupUserTasks(groupId, id),
					getGroupUserApprovals(groupId, id),
					getUserProfile(groupId, id),
					getGroupUserRolesWithTasks(groupId, id),
					getGroupRoles(groupId),
					getGroupUserDocuments(groupId, id),
				]);

				const userTasks = userTasksResponse.data;
				setActiveUserTodoTasks(userTasks.filter((task) => task.status === 'todo'));
				setActiveUserTasks(userTasks);

				const userApprovals = userApprovalsResponse.data;
				setActiveUserTodoApprovals(userApprovals.filter((approval) => approval.status === 'todo'));
				setActiveUserApprovals(userApprovals);

				setRolesWithTasks(userRolesWithTasksResponse.data);
				setActiveUserProfile(userProfileResponse);
				setActiveGroupRoles(groupRoles);
				setActiveUserDocuments(userDocuments);
			} finally {
				setIsDrawerLoading(false);
			}
		},
		[usersData?.data, groups],
	);

	const handleSort = ([sort]: [{ field: string; sort: 'asc' | 'desc' }]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: { roleIds?: string[] | string; search?: string }) => {
		updateQueryParams(filterData);
	};

	const handleGroupFilterChange = (groupIds: string | string[]) => {
		updateQueryParams({ groupId: groupIds });
	};

	const handleSupervisorFilterChange = (supervisorIds: string | string[]) => {
		updateQueryParams({ supervisorId: supervisorIds });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleUserStatusFilterChange = (userStatus: 'active' | 'archived') => {
		updateQueryParams({ status: userStatus });
	};

	const handleCloseDrawer = () => {
		setActiveUser(undefined);
		setActiveUserGroupId(undefined);
		setActiveUserTasks([]);
		setActiveUserTodoTasks([]);
		setActiveUserApprovals([]);
		setActiveUserTodoApprovals([]);
		setRolesWithTasks([]);
		setActiveUserProfile(undefined);
		setActiveUserDocuments([]);
		setIsEditingRoles(false);
		setIsDrawerLoading(false);
		setActiveGroupRoles([]);
	};

	const handleEditRoles = () => {
		setIsEditingRoles((prev) => !prev);
	};

	const handleEditUser = () => {
		setIsEditingUser((prev) => !prev);
	};

	const handleRolesAdd = async (roleIds: string[]) => {
		if (!activeUser || !activeUserGroupId) return;
		await addRolesToUser(activeUserGroupId, activeUser.id, roleIds);
		setRoleDialogOpen(false);
		revalidateUsers();
	};

	const handleDeleteUser = async () => {
		if (!activeUser) return;
		try {
			await deleteUser(activeUser.id);
			EventEmitter.emit('toast', { type: 'success', message: 'User deleted' });
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Error deleting user' });
		}
		revalidateUsers();
		setIsDeleteUserDialogOpen(false);
	};

	const handleInviteUser = async (inviteData: InviteData) => {
		const groupId = inviteData.groupIds?.[0] || inviteGroup?.id;
		if (!groupId) return;
		try {
			const user = await inviteUser(groupId, inviteData);
			EventEmitter.emit('toast', { type: 'success', message: 'Saved' });
			revalidateUsers();
			setIsAddUserDialogOpen(false);
			return user;
		} catch (error: any) {
			EventEmitter.emit('toast', { type: 'error', message: error.message || 'Failed to invite user.' });
		}
	};

	const handleUpdateUserData = async (userData: Record<string, unknown>) => {
		if (!activeUser || !activeUserGroupId) {
			EventEmitter.emit('toast', { type: 'error', message: 'Something went wrong. Refresh the page and try again.' });
			return;
		}
		try {
			await apiUpdateUserData(activeUserGroupId, activeUser.id, userData);
			revalidateUsers();
			setIsEditingUser(false);
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update user.' });
		}
	};

	const extractAllRoleIds = (cascadingRoles: Role[]): string[] => {
		return cascadingRoles.map((role) => role.id);
	};

	const handleRoleRevoke = async (roleIds: string[]) => {
		if (!activeUser || !activeUserGroupId) return;
		try {
			await revokeRolesFromUser(activeUserGroupId, activeUser.id, roleIds);
			EventEmitter.emit('toast', { type: 'success', message: roleIds.length > 1 ? 'Roles removed' : 'Role removed' });
		} catch {
			EventEmitter.emit('toast', {
				type: 'error',
				message: roleIds.length > 1 ? 'Error deleting roles' : 'Error deleting role',
			});
		}
		revalidateUsers();
	};

	const handleDeleteRole = async (roleId: string) => {
		if (!activeUser) return;
		const role = activeGroupRoles.find((r) => r.id === roleId);
		const userRoles = activeUser.roles || [];
		const cascadingRoles = calculateCascadingRoleRevocations(roleId, userRoles, activeGroupRoles);
		setRoleToRevokeData({
			roleId,
			roleName: role?.name || roleId,
			cascadingRoles,
		});
	};

	const handleCancelRoleRevoke = () => {
		setRoleToRevokeData(undefined);
	};

	const handleConfirmRoleRevoke = async () => {
		if (!activeUser || !roleToRevokeData) return;
		const allRoleIds = [roleToRevokeData.roleId, ...extractAllRoleIds(roleToRevokeData.cascadingRoles)];
		await handleRoleRevoke(allRoleIds);
		setRoleToRevokeData(undefined);
	};

	const handleTaskStatusClick = (task: Task, isExecutable: boolean) => {
		if (!isExecutable) return;
		setActiveTask(task);
	};

	const handleTaskDialogClose = async () => {
		setActiveTask(undefined);
		if (activeUser && activeUserGroupId) {
			setActiveUserTasks((await getGroupUserTasks(activeUserGroupId, activeUser.id)).data);
			setRolesWithTasks((await getGroupUserRolesWithTasks(activeUserGroupId, activeUser.id)).data);
		}
	};

	const handleTaskSelected = (taskId: string) => {
		if (!activeUser) return;
		const task = activeUserTasks.find((t) => t.id === taskId);
		if (!task) return;
		const { isExecutable } = handleTaskExecution(task);
		handleTaskStatusClick(task, isExecutable);
	};

	const handleUpdateBadge = () => {
		setIsBadgeDialogOpen(true);
	};

	const saveBadgeId = async (badgeId: string) => {
		if (!activeUserProfile || !activeUserGroupId) return;
		try {
			await updateBadgeId(activeUserGroupId, activeUserProfile.user.id, badgeId);
		} catch (err: unknown) {
			const errorMessage = err instanceof Error ? err.message : 'Unknown error';
			EventEmitter.emit('toast', { type: 'error', message: `Update failed (${errorMessage})` });
		}
		setActiveUserProfile(await getUserProfile(activeUserGroupId, activeUserProfile.user.id));
	};

	const deleteBadgeIdHandler = async () => {
		if (!activeUserProfile || !activeUserGroupId) return;
		try {
			await updateBadgeId(activeUserGroupId, activeUserProfile.user.id, '');
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to delete badge id.' });
		}
		setActiveUserProfile(await getUserProfile(activeUserGroupId, activeUserProfile.user.id));
	};

	const handleCloseBadgeDialog = () => {
		setIsBadgeDialogOpen(false);
	};

	const handleTaskExecution = (task: Task, isApproval = false) => {
		if (task.status === 'expired')
			return { isExecutable: false, tooltipText: isApproval ? 'This approval is expired' : 'This task is expired' };
		if (task.status === 'locked') return { isExecutable: false, tooltipText: 'Approval needed' };
		if (task.status !== 'todo') {
			const tooltipText = isApproval ? 'This approval is complete' : 'This task is done';
			return { isExecutable: false, tooltipText };
		}
		if (!task.executePermission?.isAllowed)
			return { isExecutable: false, tooltipText: task.executePermission?.denyReason ?? '' };
		return { isExecutable: true, tooltipText: '' };
	};

	const handleRestoreUserClick = () => {
		setIsRestoreUserDialogOpen(true);
	};

	const handleCloseRestoreUserDialog = () => {
		setIsRestoreUserDialogOpen(false);
	};

	const restoreUserHandler = async () => {
		if (!activeUser || !activeUserGroupId) return;
		await apiRestoreUser(activeUserGroupId, activeUser.id);
		revalidateUsers();
		setIsRestoreUserDialogOpen(false);
	};

	const renderPhoneNumber = () => {
		try {
			if (activeUser?.phone) {
				return parsePhoneNumber(activeUser.phone)?.formatInternational();
			} else if (activeUser?.userData?.fixedPhoneLine) {
				return parsePhoneNumber(activeUser.userData.fixedPhoneLine as string)?.formatInternational();
			}
			return '-';
		} catch {
			return activeUser?.phone || (activeUser?.userData?.fixedPhoneLine as string) || '-';
		}
	};

	const baseColumns = [
		{
			field: 'status',
			headerName: '',
			renderCell: (c: { row: User }) => {
				if (c.row.archived) return <InventoryIcon fontSize="small" sx={{ opacity: 0.5 }} />;
				if (c.row.markedForReview) return <ReportIcon fontSize="small" color="error" />;
				if (c.row.badgeId) return <BadgeIcon fontSize="small" sx={{ opacity: 0.5 }} />;
				return null;
			},
			width: 5,
		},
		{
			field: 'firstName',
			headerName: 'First name',
			flex: 1,
		},
		{
			field: 'lastName',
			headerName: 'Last name',
			flex: 1,
		},
		{
			field: 'groups',
			headerName: 'Company',
			flex: 1,
			renderCell: (c: { value?: Group[] }) =>
				c.value?.map((g) => <GroupChip key={g.id} group={g} disableNavigation navigationDisabledTitle="" />),
			sortable: false,
		},
		...(showSupervisor
			? [
					{
						field: 'supervisor',
						headerName: 'Supervisor',
						flex: 1,
						renderCell: (c: { value?: User }) =>
							c.value ? <SupervisorChip supervisor={c.value} disableNavigation /> : null,
						sortable: false,
					},
				]
			: []),
		{
			field: 'roles',
			headerName: 'Roles',
			flex: 1,
			renderCell: (c: { value: Role[]; row: User }) =>
				c.value?.map((r) => (
					<RoleChip key={r.name} role={r} userMarkedForReview={!!c.row.markedForReview || c.row.status === 'blocked'} />
				)),
			sortable: false,
		},
		{
			field: 'updatedAt',
			headerName: 'Updated',
			width: 120,
			valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
		},
	];

	const groupFilterOptions = groups.map((g: Group) => ({
		id: g.id,
		name: g.name,
	}));

	const [supervisorFilterOptions, setSupervisorFilterOptions] = useState<{ id: string; name: string }[]>([]);

	useEffect(() => {
		if (!usersData?.data) return;
		setSupervisorFilterOptions((prev) => {
			const seen = new Map(prev.map((s) => [s.id, s]));
			usersData.data.forEach((user: User) => {
				const sup = (user as any).supervisor;
				if (sup?.id && !seen.has(sup.id)) {
					const name = `${sup.firstName || ''} ${sup.lastName || ''}`.trim() || sup.id;
					seen.set(sup.id, { id: sup.id, name });
				}
			});
			const next = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
			if (next.length === prev.length && next.every((s, i) => s.id === prev[i]?.id)) return prev;
			return next;
		});
	}, [usersData?.data]);

	if (!groups || groups.length === 0) {
		return (
			<div className="p-4 text-center flex flex-col items-center justify-center h-full">
				<div>
					<img src="/public/blank_canvas.svg" className="h-150 m-auto mb-4 mt-2" alt="No groups" />
				</div>
				<div className="font-bold opacity-50">
					Your supervisor access is not yet fully configured.
					<br />
					Contact your administrator.
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="mt-4 flex flex-row flex-wrap gap-2">
				<FilterSearch
					key="search"
					label="Search User"
					defaultValue={search.search || ''}
					onChange={debounce((searchInput: string) => {
						handleSearchChange(searchInput);
					}, 350)}
					sx={{ width: '20%', minWidth: '20%', flexGrow: 1 }}
				/>
				<FilterAutoCompleteById
					key="groupFilter"
					label="Filter Company"
					options={groupFilterOptions}
					multiple
					value={search.groupId || []}
					onChange={(groupIds) => handleGroupFilterChange(groupIds)}
					sx={{ width: '20%', minWidth: '20%', flexGrow: 1 }}
				/>
				{showSupervisor && (
					<FilterAutoCompleteById
						key="supervisorFilter"
						label="Filter Supervisor"
						options={supervisorFilterOptions}
						multiple
						value={search.supervisorId || []}
						onChange={(supervisorIds) => handleSupervisorFilterChange(supervisorIds)}
						sx={{ width: '20%', minWidth: '20%', flexGrow: 1 }}
					/>
				)}
				<FilterSelect
					options={[
						{ label: 'Active Users', value: 'active' },
						{ label: 'Archived Users', value: 'archived' },
					]}
					value={userStatusFilter}
					onChange={(userStatus) => handleUserStatusFilterChange(userStatus as 'active' | 'archived')}
					sx={{ width: 330 }}
				/>
				{!isBasf && (
					<Button startIcon={<AddIcon />} variant="outlined" onClick={() => setIsAddUserDialogOpen(true)}>
						Add user
					</Button>
				)}
			</div>

			<Paper variant="outlined" sx={{ marginTop: 2 }}>
				<DataGrid
					data={usersData?.data || []}
					columns={baseColumns}
					isLoading={isLoading}
					rowCount={usersData?.count || 0}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={handlePaginationModelChange}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(search.sort || 'updatedAt:desc')]}
					pageSize={usersData?.pageSize || 50}
					pageSizeOptions={[usersData?.pageSize || 50]}
					page={usersData?.page || 0}
					onRowClick={handleRowClick}
					pagination
					getRowClassName={(p) => (p.row.markedForReview ? 'error' : '')}
					onReload={revalidateUsers}
				/>
			</Paper>

			{/* User Detail Drawer */}
			<Drawer onClose={handleCloseDrawer} open={!!activeUser} anchor="right">
				{activeUser && (
					<div className="p-4 pt-3 pb-3 flex flex-col gap-2 w-[620px]">
						<div className="flex items-center gap-2">
							<div className="flex items-center gap-2">
								<IconButton sx={{ borderRadius: 1 }} onClick={handleCloseDrawer}>
									<CloseIcon />
								</IconButton>
								<Typography variant="h4">User Details</Typography>
							</div>
							<div className="flex-grow" />
							{activeUser.status === 'active' && verifyPermission(PERMISSIONS.adminUsersArchive) && (
								<IconButton onClick={() => setIsDeleteUserDialogOpen(true)} sx={{ borderRadius: 1 }}>
									<DeleteIcon />
								</IconButton>
							)}
						</div>

						{isDrawerLoading ? (
							<div className="flex justify-center items-center py-16">
								<CircularProgress />
							</div>
						) : (
							<>
								{activeUserTodoTasks.length > 0 && (
									<Alert severity="warning" variant="filled" sx={{ marginBottom: 2 }}>
										This user has one or more pending tasks.
									</Alert>
								)}
								{activeUserTodoApprovals.length > 0 && (
									<Alert severity="warning" variant="filled" sx={{ marginBottom: 2 }}>
										This user has one or more pending approvals.
									</Alert>
								)}
								{Array.isArray(activeUser.markedForReview) && activeUser.markedForReview.includes('blocklist') && (
									<Alert severity="error" variant="filled" sx={{ marginBottom: 2 }}>
										This user matches one or more blocklist rules or the user's identification document originates from
										a blocked country. Contact your administrator
									</Alert>
								)}
								{Array.isArray(activeUser.markedForReview) && !activeUser.markedForReview.includes('blocklist') && (
									<Alert severity="error" variant="filled" sx={{ marginBottom: 2 }}>
										This user is currently locked and requires to be reviewed before receiving access.
									</Alert>
								)}
								{activeUser.status === 'archived' && (
									<Alert
										severity="info"
										variant="filled"
										sx={{
											'.MuiAlert-message': {
												display: 'flex',
												justifyContent: 'space-between',
												flexGrow: 1,
												alignItems: 'center',
											},
											'.MuiAlert-icon': { alignItems: 'center' },
										}}
									>
										<div>This user is archived.</div>
										<Button className="!text-white" variant="outlined" onClick={handleRestoreUserClick}>
											Restore
										</Button>
									</Alert>
								)}

								{/* User Info Card */}
								<Paper className="p-4 min-w-[570px] flex" variant="outlined">
									<div className="flex w-[200px] justify-self-stretch mr-3">
										<Avatar
											variant="square"
											sx={{
												width: 200,
												height: 200,
												borderRadius: 1,
												borderColor: 'action.disabledBackground',
												borderWidth: 1,
												borderStyle: 'solid',
											}}
											src={activeUserProfile?.faceImage?.link || ''}
										/>
									</div>
									<div className="flex flex-col gap-1 flex-grow">
										<div className="flex items-center gap-3 justify-between flex-grow">
											<Typography variant="h6" mb={0.5} sx={{ fontWeight: 'bold' }}>
												{activeUser.firstName} {activeUser.lastName}
											</Typography>
											<ToggleButtonGroup value={isEditingUser} exclusive>
												<ToggleButton value={true} onClick={handleEditUser} sx={{ border: 'none !important' }}>
													<EditIcon sx={{ fontSize: 20 }} />
												</ToggleButton>
											</ToggleButtonGroup>
										</div>
										<div className="flex flex-col">
											<Typography variant="caption">Email</Typography>
											<Typography variant="subtitle2" sx={{ minHeight: 24 }}>
												{activeUser.email || '-'}
											</Typography>
										</div>
										<div className="flex flex-col">
											<Typography variant="caption">Phone</Typography>
											<Typography variant="subtitle2" sx={{ minHeight: 24 }}>
												{renderPhoneNumber()}
											</Typography>
										</div>
										{usesRFID &&
											verifyPermission(PERMISSIONS.supervisorSecurityAccessManagement) &&
											!!activeUserProfile && (
												<div className="flex flex-col">
													<Typography variant="caption">Badge ID</Typography>
													<div>
														<Chip
															label={toFormattedHex(activeUserProfile?.user.badgeId)}
															size="small"
															sx={{ margin: '.3em', marginLeft: 0 }}
															icon={<ContactlessIcon />}
															deleteIcon={<EditIcon />}
															onDelete={handleUpdateBadge}
														/>
													</div>
												</div>
											)}
										{activeUser.shortId && (
											<div className="flex flex-col">
												<Typography variant="caption">Short ID</Typography>
												<Typography variant="subtitle2" sx={{ minHeight: 24 }}>
													{activeUser.shortId}
												</Typography>
											</div>
										)}
									</div>
								</Paper>

								{/* Roles Card */}
								<Paper className="p-4" variant="outlined">
									<div className="flex items-center justify-between">
										<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
											Roles
										</Typography>
										<ToggleButtonGroup value={isEditingRoles} exclusive>
											<ToggleButton value={true} onClick={handleEditRoles} sx={{ border: 'none !important' }}>
												<EditIcon sx={{ fontSize: 20 }} />
											</ToggleButton>
										</ToggleButtonGroup>
									</div>
									<div>
										{activeUser.roles?.length > 0 || isEditingRoles
											? activeUser.roles?.map((r) => (
													<RoleChip
														key={r.name}
														role={r}
														sx={{ margin: '.3em', marginLeft: 0 }}
														onDelete={isEditingRoles ? () => handleDeleteRole(r.id) : undefined}
													/>
												))
											: '-'}
										{isEditingRoles && (
											<Chip
												icon={<AddIcon />}
												label="Add Roles"
												size="small"
												clickable
												onClick={() => setRoleDialogOpen(true)}
											/>
										)}
									</div>
								</Paper>

								{/* Tasks Card */}
								<Paper className="p-4" variant="outlined">
									<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
										Tasks by User Role
									</Typography>
									<div className="flex flex-col gap-1">
										{rolesWithTasks.length > 0 ? (
											<UserRolesWithTasksAccordion
												rolesWithTasks={rolesWithTasks}
												onTaskSelected={handleTaskSelected}
												onReload={revalidateUsers}
												searchParams={search as Record<string, string | undefined>}
												expandRoles={false}
												hideDetailColumns={true}
											/>
										) : (
											'-'
										)}
									</div>
								</Paper>

								{/* Approvals Card */}
								{activeUserApprovals.length > 0 && (
									<Paper sx={{ padding: 2 }} variant="outlined">
										<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
											Approvals
										</Typography>
										<div className="flex flex-col gap-1">
											{activeUserApprovals?.map((approval) => {
												const { isExecutable, tooltipText } = handleTaskExecution(approval, true);
												return (
													<div className="flex justify-between" key={approval.id}>
														<Typography variant="subtitle2">{approval.name}</Typography>
														<ConditionalWrapper
															condition={!isExecutable}
															Wrapper={({ children }) => <Tooltip text={tooltipText}>{children}</Tooltip>}
														>
															<Chip
																sx={{
																	width: 120,
																	...(!isExecutable && { cursor: 'default' }),
																}}
																label={states[approval.status]}
																color={colors[approval.status]}
																onClick={isExecutable ? () => handleTaskStatusClick(approval, isExecutable) : undefined}
															/>
														</ConditionalWrapper>
													</div>
												);
											})}
										</div>
									</Paper>
								)}

								{/* Documents Card */}
								<Paper sx={{ padding: 2 }} variant="outlined">
									<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
										Documents
									</Typography>
									<div className="flex flex-col gap-1">
										{activeUserDocuments.length > 0
											? activeUserDocuments.map((document) => (
													<Button
														key={document.id}
														sx={{ display: 'flex', alignItems: 'center', textTransform: 'initial', gap: 1 }}
														onClick={() => setSelectedFile(document)}
													>
														<FileState status={document.status} />
														<Typography variant="subtitle2" sx={{ flexGrow: 1, textAlign: 'left' }}>
															{document.fileName}
														</Typography>
														<Typography variant="subtitle2">
															{documentTypes.find((dt) => dt.id === document.type)?.name || document.type}
														</Typography>
													</Button>
												))
											: '-'}
									</div>
								</Paper>
							</>
						)}
					</div>
				)}
			</Drawer>

			{/* Dialogs */}
			<TaskDialog
				task={activeTask}
				onClose={handleTaskDialogClose}
				userId={activeUser?.id || ''}
				companyId={activeUserGroupId || ''}
				lng="en"
			/>

			<ConfirmDialog
				isOpen={!!roleToRevokeData}
				cancelAction={handleCancelRoleRevoke}
				confirmAction={handleConfirmRoleRevoke}
				cancelLabel="Cancel"
				confirmLabel="Delete"
			>
				Are you sure you want to delete the role <strong>{roleToRevokeData?.roleName}</strong>?
				{roleToRevokeData && <CascadingRoleRevocationAlert cascadingRoles={roleToRevokeData.cascadingRoles} />}
			</ConfirmDialog>

			{activeUser && (
				<UserForm
					isOpen={isEditingUser}
					isAdmin={false}
					onClose={() => setIsEditingUser(false)}
					onSubmit={handleUpdateUserData}
					data={activeUser}
					defaultCountry={defaultCountry}
				/>
			)}

			{activeUser && (
				<ConfirmDialog
					isOpen={isDeleteUserDialogOpen}
					cancelAction={() => setIsDeleteUserDialogOpen(false)}
					confirmAction={handleDeleteUser}
					cancelLabel="Cancel"
					confirmLabel="Delete"
				>
					Are you sure you want to delete this user?
				</ConfirmDialog>
			)}

			{activeUser && (
				<AddToRoleDialog
					isOpen={isRoleDialogOpen}
					onClose={() => setRoleDialogOpen(false)}
					userData={activeUser}
					roles={activeGroupRoles}
					onRolesAdd={handleRolesAdd}
				/>
			)}

			{activeUser && selectedFile && activeUserGroupId && (
				<DocumentViewer
					document={selectedFile}
					onClose={() => setSelectedFile(null)}
					types={documentTypes as DocumentType[]}
					apiUrlBase={`/api/supervisor-portal/groups/${activeUserGroupId}/users/${activeUser.id}/files`}
					onRevalidateDocument={() => {}}
					tasks={[]}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.supervisorView]),
						fullView: verifyPermission([PERMISSIONS.supervisorView]),
						sensitiveView: false,
						sensitiveEdit: false,
						sensitiveArchive: false,
						edit: false,
						archive: false,
						review: false,
						enableReview: false,
					}}
				/>
			)}

			<BadgeDialog
				isOpen={isBadgeDialogOpen && !!activeUserProfile}
				originalBadgeId={activeUserProfile?.user.badgeId}
				onSave={saveBadgeId}
				onDelete={deleteBadgeIdHandler}
				onClose={handleCloseBadgeDialog}
			/>

			<ConfirmDialog
				isOpen={isRestoreUserDialogOpen}
				cancelAction={handleCloseRestoreUserDialog}
				confirmAction={restoreUserHandler}
			>
				Are you sure you want to restore this user?
			</ConfirmDialog>

			<InviteForm
				onClose={() => {
					revalidateUsers();
					setIsAddUserDialogOpen(false);
				}}
				isOpen={isAddUserDialogOpen}
				onInvite={handleInviteUser}
				selectedGroup={inviteGroup}
				authenticatedUser={authenticatedUser!}
				defaultCountry={defaultCountry}
				permissions={{
					canCreateWithoutEmail: false,
					isUserAdmin: false,
					canImport: false,
				}}
			/>
		</>
	);
}
